import {makeAutoObservable} from 'mobx';
import {ElasticSearchResult, SpecialStoreFilter} from '../../storepage.def';
import axios from 'axios';
import storePageDataSource from '../../storepage-datasource';
import {getAntiForgeryToken} from '../../../util';

declare var __special_id: number;
declare var __fastShipIds: number[];
declare var __InstitutionID: number;
declare var __mouldingOptions: string[] | undefined;
declare var __matOptions: string[] | undefined;
declare var __prefilter: number;

export enum FrameType {
    Tassel = 'tassel',
    Diploma = 'diploma',
    Certificate = 'certificate',
}

class FiltersStore {
    public filters: SpecialStoreFilter = {
        specialId: __special_id,
        finish: '',
        matcombo: '',
        priceMin: 0,
        priceMax: 500,
        inst: '',
        sort: '',
        pageNumber: 1,
        InstitutionID: __InstitutionID,
        frameType: [],
        graduationYear: '',
    };
    public loading: boolean;
    public selectedFinish: string[] = [];
    public selectedMatCombo: string[] = [];
    public selectedFrameType: FrameType[] = [];
    public selectedGraduationYear: string = '';
    public graduationYearError: string = '';
    public debounceTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor() {
        makeAutoObservable(this);
        this.applyUrlFilters();

        const params = new URLSearchParams(window.location.search);
        const _search = params.get('s');
        if (_search) {
            this.filters.inst = _search;
            this.onSchoolSearch(_search);
            const input = document.querySelector('#school-search') as HTMLInputElement;
            if (input) {
                input.value = _search;
            }
            setTimeout(function () {
                const input = document.querySelector('#school-search') as HTMLInputElement;
                if (input) {
                    input.value = _search;
                }
            }, 250);
            setTimeout(function () {
                const input = document.querySelector('#school-search') as HTMLInputElement;
                if (input) {
                    input.value = _search;
                }
            }, 1500);
        }
    }

    applyUrlFilters() {
        try {
            const params = new URLSearchParams(window.location.search);
            let type = params.get('t');

            switch (type) {
                case 'best-sellers':
                    this.getData = this.wrapBestSellersFilter(this.getData.bind(this));
                    break;

                case 'single-tassel':
                    this.selectedFrameType = [FrameType.Tassel];
                    this.filters.frameType = [FrameType.Tassel];
                    break;

                case 'diploma-frames':
                    this.selectedFrameType = [FrameType.Diploma];
                    this.filters.frameType = [FrameType.Diploma];
                    break;

                case 'certificate-frames':
                    this.selectedFrameType = [FrameType.Certificate];
                    this.filters.frameType = [FrameType.Certificate];
                    break;

                default:
                    break;
            }
        } catch (err) {
            console.error(err);
        }

        try {
            // console.log('checking __prefilter', __prefilter);
            switch (__prefilter) {
                case 1:
                    this.getData = this.wrapBestSellersFilter(this.getData.bind(this));
                    break;

                case 2:
                    this.selectedFrameType = [FrameType.Diploma];
                    this.filters.frameType = [FrameType.Diploma];
                    break;

                case 3:
                    this.selectedFrameType = [FrameType.Certificate];
                    this.filters.frameType = [FrameType.Certificate];
                    break;

                case 4:
                    this.selectedFrameType = [FrameType.Tassel];
                    this.filters.frameType = [FrameType.Tassel];
                    break;

                default:
                    break;
            }
        } catch (err) {
            console.error(err);
        }
    }

    wrapBestSellersFilter(originalGetData: () => Promise<void>) {
        return async () => {
            await originalGetData();

            storePageDataSource.products.hits.hits = storePageDataSource.products.hits.hits.filter(i => {
                const item = i._source;

                return item.hideAisleA || item.is_fast_ship || item.RetailQtySold > 3;
            });
        };
    }

    async getData() {
        this.loading = true;

        try {
            if (__InstitutionID) {
                this.filters.InstitutionID = __InstitutionID;
            }

            const config = {
                headers: {
                    RequestVerificationToken: getAntiForgeryToken(),
                },
            };

            const response = await axios.post('/api/special_store', this.filters, config);

            // Check if the response contains an Elasticsearch error
            if (response.data && response.data.error) {
                console.error('Elasticsearch Error:', response.data);
                this.loading = false;
                return;
            }

            if (response.status === 500 || response.status === 400) {
                console.error('API Error:', response.data);
                this.loading = false;
                return;
            }

            storePageDataSource.products = response.data as ElasticSearchResult;
            storePageDataSource.getExtra();

            for (let i = 0; i < storePageDataSource.products.hits.hits.length; i++) {
                const pid = storePageDataSource.products.hits.hits[i]._source.ProductID;
                const isFastShip = __fastShipIds.includes(pid);

                storePageDataSource.products.hits.hits[i]._source.is_fast_ship = isFastShip;
                storePageDataSource.products.hits.hits[i]._source.sortFast = isFastShip ? -1 : storePageDataSource.products.hits.hits[i]._source.sortBest;
            }

            this.loading = false;
        } catch (err) {
            console.error(err);

            this.loading = false;

            return err;
        }
    }

    getMatOptions() {
        let options: string[] | undefined = typeof __matOptions !== 'undefined' && Array.isArray(__matOptions) ? __matOptions : undefined;

        if (!options || options.length === 0) {
            options = storePageDataSource.products.hits.hits
                .filter(x => x._source && x._source.MatComboName)
                .map(p => p._source.MatComboName)
                .filter(option => option && option.trim() !== '');
        }

        storePageDataSource.matOptions = ['', ...new Set(options)];
    }

    getMouldingOptions() {
        let options: string[] | undefined = typeof __mouldingOptions !== 'undefined' && Array.isArray(__mouldingOptions) ? __mouldingOptions : undefined;

        if (!options || options.length === 0) {
            options = storePageDataSource.products.hits.hits
                .filter(x => x._source && x._source.MouldingFinish)
                .map(p => p._source.MouldingFinish)
                .filter(option => option && option.trim() !== '');
        }

        storePageDataSource.mouldingOptions = ['', ...new Set(options)];
    }

    async updateFilter<Key extends keyof SpecialStoreFilter>(key: Key, value: SpecialStoreFilter[Key]) {
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
        }

        const timeout = key === 'inst' || key === 'priceMin' || key === 'priceMax' || key === 'graduationYear' ? 300 : 0;

        this.debounceTimeout = setTimeout(() => {
            this.applyFilterAndHandlePagination(key, value);
        }, timeout);
    }

    async applyFilterAndHandlePagination<Key extends keyof SpecialStoreFilter>(key: Key, value: SpecialStoreFilter[Key]) {
        this.filters[key] = value;
        await this.getData();

        while (key !== 'pageNumber' && storePageDataSource.products.hits.hits.length === 0 && this.filters.pageNumber > 1) {
            this.filters.pageNumber--;
            await this.getData();
        }
    }

    async finishChanged(checked: boolean, value: string) {
        if (checked) {
            if (value === '') {
                // "All Mouldings" selected - clear all other selections
                this.selectedFinish = [''];
            } else {
                // Remove "All Mouldings" if it was selected and add the specific selection
                this.selectedFinish = this.selectedFinish.filter(f => f !== '');
                if (!this.selectedFinish.includes(value)) {
                    this.selectedFinish.push(value);
                }
            }
        } else {
            this.selectedFinish = this.selectedFinish.filter(f => f !== value);
        }

        await this.updateFilter('finish', this.selectedFinish.join(','));
    }

    async matComboChanged(checked: boolean, value: string) {
        if (checked) {
            if (value === '') {
                // "All Mats" selected - clear all other selections
                this.selectedMatCombo = [''];
            } else {
                // Remove "All Mats" if it was selected and add the specific selection
                this.selectedMatCombo = this.selectedMatCombo.filter(m => m !== '');
                if (!this.selectedMatCombo.includes(value)) {
                    this.selectedMatCombo.push(value);
                }
            }
        } else {
            this.selectedMatCombo = this.selectedMatCombo.filter(m => m !== value);
        }

        await this.updateFilter('matcombo', this.selectedMatCombo.join(','));
    }

    async frameTypeChanged(checked: boolean, type: FrameType) {
        if (checked) {
            if (!this.selectedFrameType.includes(type)) {
                this.selectedFrameType.push(type);
            }
        } else {
            this.selectedFrameType = this.selectedFrameType.filter(f => f !== type);
        }

        await this.updateFilter('frameType', this.selectedFrameType);
    }

    async graduationYearChanged(value: string) {
        this.selectedGraduationYear = value;

        if (value && (+value < 1900 || +value > 2100)) {
            this.graduationYearError = 'Year must be between 1900 and 2100.';
        } else {
            this.graduationYearError = '';
            await this.updateFilter('graduationYear', value);
        }
    }

    async onSchoolSearch(inst: string) {
        if (inst.length > 1 || !inst.length) {
            await this.updateFilter('inst', inst);
        }
    }
}

const filtersStore = new FiltersStore();

export default filtersStore;
